// ===================================================================
// PA GRANTS — Analytics
// Funding overview, category/phase breakdowns, compliance, risk
// ===================================================================

import { useEffect, useMemo } from 'react';
import {
  Paper, Text, Group, Stack, Badge, SimpleGrid, Loader, Center, Title,
  Table, Progress, ThemeIcon,
} from '../../components/gab/GabComponents';
import {
  IconCurrencyDollar, IconFileCheck, IconAlertTriangle, IconShieldCheck,
} from '@tabler/icons-react';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useDisasterFilter } from '../../hooks/useActiveDisaster';
import {
  DAMAGE_CATEGORY_COLORS, DAMAGE_CATEGORY_LABELS, type DamageCategory,
  PROJECT_PHASE_LABELS, type ProjectPhase,
} from '../../types/pagrants';
import { primary } from '../../lib/colors';

const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const pct = (a: number, b: number) => b > 0 ? Math.round((a / b) * 100) : 0;

const PHASE_COLORS: Record<string, string> = {
  formulation: '#339af0', review: '#fcc419', obligation: '#099268',
  work_in_progress: '#f76707', closeout: '#7c3aed', appeal: '#868e96',
};

export default function Analytics() {
  const {
    organizations, projects: allProjects, projectCosts, compliance, insurance,
    loadingProjects, fetchAll,
  } = usePAGrantsStore();
  const projects = useDisasterFilter(allProjects).filter(p => p.workType !== 'hmgp');

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Org lookup
  const orgMap = useMemo(() => {
    const m = new Map<string, string>();
    organizations.forEach(o => m.set(String(o.Rid), o.orgName));
    return m;
  }, [organizations]);

  // ── Funding Overview ───────────────────────────────────────
  const funding = useMemo(() => {
    const totalEstimated = projects.reduce((s, p) => s + (parseFloat(p.estimatedCost) || 0), 0);
    const totalObligated = projects.reduce((s, p) => s + (parseFloat(p.obligatedAmount) || 0), 0);
    const totalDrawdown = projects.reduce((s, p) => s + (parseFloat(p.drawdownAmount) || 0), 0);
    const totalFederal = projects.reduce((s, p) => s + (parseFloat(p.federalShareAmount) || 0), 0);
    const totalApplicant = projects.reduce((s, p) => s + (parseFloat(p.applicantShareAmount) || 0), 0);
    const totalInsDeduction = projects.reduce((s, p) => s + (parseFloat(p.insuranceDeduction) || 0), 0);
    const totalNetEligible = projects.reduce((s, p) => s + (parseFloat(p.netEligibleCost) || 0), 0);
    const remaining = totalObligated - totalDrawdown;
    return { totalEstimated, totalObligated, totalDrawdown, totalFederal, totalApplicant, totalInsDeduction, totalNetEligible, remaining };
  }, [projects]);

  // ── By Category ────────────────────────────────────────────
  const byCategory = useMemo(() => {
    const cats: DamageCategory[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
    const maxCost = Math.max(...cats.map(c => projects.filter(p => p.category === c).reduce((s, p) => s + (parseFloat(p.estimatedCost) || 0), 0)), 1);
    return cats.map(cat => {
      const cp = projects.filter(p => p.category === cat);
      const est = cp.reduce((s, p) => s + (parseFloat(p.estimatedCost) || 0), 0);
      const obl = cp.reduce((s, p) => s + (parseFloat(p.obligatedAmount) || 0), 0);
      return { cat, count: cp.length, est, obl, barPct: pct(est, maxCost) };
    }).filter(r => r.count > 0);
  }, [projects]);

  // ── By Phase ───────────────────────────────────────────────
  const byPhase = useMemo(() => {
    const phases: ProjectPhase[] = ['formulation', 'review', 'obligation', 'work_in_progress', 'closeout'];
    return phases.map(ph => {
      const pp = projects.filter(p => p.phase === ph);
      const est = pp.reduce((s, p) => s + (parseFloat(p.estimatedCost) || 0), 0);
      return { phase: ph, count: pp.length, est };
    });
  }, [projects]);

  // ── Applicant Comparison ───────────────────────────────────
  const applicantData = useMemo(() =>
    organizations.map(org => {
      const op = projects.filter(p => p.organizationId === String(org.Rid));
      const est = op.reduce((s, p) => s + (parseFloat(p.estimatedCost) || 0), 0);
      const obl = op.reduce((s, p) => s + (parseFloat(p.obligatedAmount) || 0), 0);
      const drawn = op.reduce((s, p) => s + (parseFloat(p.drawdownAmount) || 0), 0);
      const avgComp = op.length > 0
        ? Math.round(op.reduce((s, p) => s + (parseFloat(p.completionPctEstimate) || 0), 0) / op.length)
        : 0;
      return { name: org.orgName, role: org.orgRole, count: op.length, est, obl, drawn, avgComp };
    }).filter(r => r.count > 0).sort((a, b) => b.est - a.est),
    [organizations, projects],
  );

  // ── Cost Type Breakdown ────────────────────────────────────
  const costTypeData = useMemo(() => {
    const groups = new Map<string, { total: number; count: number }>();
    projectCosts.forEach(c => {
      const t = c.costType || 'other';
      const cur = groups.get(t) || { total: 0, count: 0 };
      cur.total += parseFloat(c.totalCost) || 0;
      cur.count += 1;
      groups.set(t, cur);
    });
    const costLabels: Record<string, string> = {
      contract: 'Contracts', force_account_labor: 'Force Account Labor',
      force_account_equipment: 'Force Account Equipment', materials: 'Materials',
      rented_equip: 'Rented Equipment', donated: 'Donated Resources', other: 'Other',
    };
    return Array.from(groups.entries())
      .map(([type, data]) => ({ type, label: costLabels[type] || type, ...data }))
      .sort((a, b) => b.total - a.total);
  }, [projectCosts]);

  const costTotal = costTypeData.reduce((s, c) => s + c.total, 0);

  // ── Compliance Overview ────────────────────────────────────
  const complianceStats = useMemo(() => {
    const total = compliance.length;
    const nepaComplete = compliance.filter(c => c.nepaStatus === 'complete').length;
    const nepaInProg = compliance.filter(c => c.nepaStatus === 'in_progress').length;
    const s106Complete = compliance.filter(c => c.section106Status === 'complete' || c.section106Status === 'not_applicable').length;
    const s106InProg = compliance.filter(c => c.section106Status === 'in_progress').length;
    const floodYes = compliance.filter(c => c.inFloodplain === 'Yes').length;
    const overallComplete = compliance.filter(c => c.overallComplianceStatus === 'complete').length;
    return { total, nepaComplete, nepaInProg, s106Complete, s106InProg, floodYes, overallComplete };
  }, [compliance]);

  // ── Risk Indicators ────────────────────────────────────────
  const risks = useMemo(() => {
    const now = Date.now();
    const thirtyDays = 30 * 86400000;
    const deadlineSoon = projects.filter(p => {
      if (!p.deadline) return false;
      const d = new Date(p.deadline).getTime();
      return !isNaN(d) && d > now && d - now < thirtyDays;
    });
    const overdue = projects.filter(p => {
      if (!p.deadline) return false;
      const d = new Date(p.deadline).getTime();
      return !isNaN(d) && d < now && p.phase !== 'closeout';
    });
    const lowCompletion = projects.filter(p =>
      (parseFloat(p.completionPctEstimate) || 0) < 20 && p.phase === 'work_in_progress'
    );
    const unobligated = projects.filter(p =>
      (parseFloat(p.estimatedCost) || 0) > 100000 && (parseFloat(p.obligatedAmount) || 0) === 0
    );
    const noInsurance = projects.filter(p => {
      const deduction = parseFloat(p.insuranceDeduction) || 0;
      if (deduction === 0) return false;
      return !insurance.some(i => i.projectId === String(p.Rid));
    });
    return { deadlineSoon, overdue, lowCompletion, unobligated, noInsurance };
  }, [projects, insurance]);

  if (loadingProjects && projects.length === 0) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }

  return (
    <Stack gap="lg">
      <Title order={3} c="black">PA Grants Analytics</Title>

      {/* ── Funding Overview ───────────────────────────────── */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        {[
          { label: 'Total Estimated', value: funding.totalEstimated, color: primary[800], icon: IconCurrencyDollar },
          { label: 'Total Obligated', value: funding.totalObligated, color: '#07963F', icon: IconFileCheck },
          { label: 'Drawn Down', value: funding.totalDrawdown, color: primary[700], icon: IconCurrencyDollar },
          { label: 'Remaining Balance', value: funding.remaining, color: '#A67103', icon: IconCurrencyDollar },
        ].map(s => (
          <Paper key={s.label} p="md" radius="md" style={{ borderLeft: `4px solid ${s.color}` }}>
            <Group gap="xs" mb={4}>
              <ThemeIcon variant="light" size="sm" color="#939598"><s.icon size={14} /></ThemeIcon>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{s.label}</Text>
            </Group>
            <Text size="xl" fw={700}>{fmt(s.value)}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* Federal / Applicant / Insurance breakdown */}
      <Paper p="md" radius="md" withBorder>
        <Text size="sm" fw={700} mb="sm">Funding Breakdown</Text>
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Stack gap={4}>
            <Group justify="space-between"><Text size="xs">Federal Share (75%)</Text><Text size="xs" fw={600}>{fmt(funding.totalFederal)}</Text></Group>
            <Progress value={pct(funding.totalFederal, funding.totalEstimated)} color="var(--color-primary-700)" size="lg" />
          </Stack>
          <Stack gap={4}>
            <Group justify="space-between"><Text size="xs">Applicant Share (25%)</Text><Text size="xs" fw={600}>{fmt(funding.totalApplicant)}</Text></Group>
            <Progress value={pct(funding.totalApplicant, funding.totalEstimated)} color="#03AC9E" size="lg" />
          </Stack>
          <Stack gap={4}>
            <Group justify="space-between"><Text size="xs">Insurance Deductions</Text><Text size="xs" fw={600}>{fmt(funding.totalInsDeduction)}</Text></Group>
            <Progress value={pct(funding.totalInsDeduction, funding.totalEstimated)} color="#F86A0B" size="lg" />
          </Stack>
        </SimpleGrid>
      </Paper>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* ── Projects by Category (horizontal bars) ──────── */}
        <Paper p="md" radius="md" withBorder>
          <Text size="sm" fw={700} mb="sm">Projects by Damage Category</Text>
          <Stack gap="sm">
            {byCategory.map(row => (
              <Stack key={row.cat} gap={2}>
                <Group justify="space-between">
                  <Group gap="xs">
                    <Badge size="xs" style={{ background: DAMAGE_CATEGORY_COLORS[row.cat], color: '#fff' }}>{row.cat}</Badge>
                    <Text size="xs">{DAMAGE_CATEGORY_LABELS[row.cat]}</Text>
                  </Group>
                  <Text size="xs" c="dimmed">{row.count} projects</Text>
                </Group>
                <div style={{ position: 'relative', height: 24, background: '#f1f5f9', borderRadius: 4 }}>
                  <div style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: `${row.barPct}%`, background: DAMAGE_CATEGORY_COLORS[row.cat],
                    borderRadius: 4, opacity: 0.8, transition: 'width 0.3s',
                  }} />
                  <Text size="xs" fw={600} style={{ position: 'absolute', right: 8, top: 3, color: row.barPct > 60 ? '#fff' : '#333' }}>
                    {fmt(row.est)}
                  </Text>
                </div>
                <Group justify="space-between">
                  <Text size="xs" c="dimmed">Obligated: {fmt(row.obl)}</Text>
                  <Text size="xs" c="dimmed">{pct(row.obl, row.est)}% obligated</Text>
                </Group>
              </Stack>
            ))}
            {byCategory.length === 0 && <Text size="xs" c="dimmed">No project data.</Text>}
          </Stack>
        </Paper>

        {/* ── Pipeline by Phase ───────────────────────────── */}
        <Paper p="md" radius="md" withBorder>
          <Text size="sm" fw={700} mb="sm">Project Pipeline by Phase</Text>
          <Stack gap="sm">
            {byPhase.map(row => (
              <Paper key={row.phase} p="sm" radius="sm" style={{ background: '#f8f9fa' }}>
                <Group justify="space-between" mb={4}>
                  <Group gap="xs">
                    <div style={{ width: 12, height: 12, borderRadius: 4, background: PHASE_COLORS[row.phase] || '#868e96' }} />
                    <Text size="sm" fw={600}>{PROJECT_PHASE_LABELS[row.phase as ProjectPhase] || row.phase}</Text>
                  </Group>
                  <Badge size="sm" variant="light">{row.count}</Badge>
                </Group>
                <Text size="xs" c="dimmed">{fmt(row.est)} estimated value</Text>
              </Paper>
            ))}
          </Stack>
          <Paper p="sm" mt="sm" radius="sm" style={{ background: 'var(--color-primary-100)' }}>
            <Group justify="space-between">
              <Text size="sm" fw={600}>Total</Text>
              <Group gap="sm">
                <Badge size="sm">{projects.length} projects</Badge>
                <Text size="sm" fw={700}>{fmt(funding.totalEstimated)}</Text>
              </Group>
            </Group>
          </Paper>
        </Paper>
      </SimpleGrid>

      {/* ── Obligation Progress by Project ─────────────────── */}
      <Paper p="md" radius="md" withBorder>
        <Text size="sm" fw={700} mb="sm">Obligation Progress by Project</Text>
        <Stack gap="xs">
          {projects.map(p => {
            const est = parseFloat(p.estimatedCost) || 0;
            const obl = parseFloat(p.obligatedAmount) || 0;
            const drawn = parseFloat(p.drawdownAmount) || 0;
            return (
              <Group key={p.Rid} wrap="nowrap" gap="sm">
                <Text size="xs" fw={600} style={{ width: 60, flexShrink: 0 }}>{p.projectNumber}</Text>
                <Text size="xs" c="dimmed" style={{ width: 120, flexShrink: 0 }} lineClamp={1}>{orgMap.get(p.organizationId) || '—'}</Text>
                <div style={{ flex: 1 }}>
                  <Progress.Root size="lg">
                    <Progress.Section value={pct(drawn, est)} color="var(--color-primary-700)">
                      <Progress.Label>{drawn > 0 ? 'Drawn' : ''}</Progress.Label>
                    </Progress.Section>
                    <Progress.Section value={pct(obl - drawn, est)} color="#03AC9E">
                      <Progress.Label>{obl > drawn ? 'Obligated' : ''}</Progress.Label>
                    </Progress.Section>
                  </Progress.Root>
                </div>
                <Text size="xs" fw={600} style={{ width: 90, textAlign: 'right', flexShrink: 0 }}>{fmt(est)}</Text>
              </Group>
            );
          })}
        </Stack>
      </Paper>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* ── Applicant Comparison ─────────────────────────── */}
        <Paper p="md" radius="md" withBorder>
          <Text size="sm" fw={700} mb="sm">Applicant Comparison</Text>
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
          <Table striped highlightOnHover>
            <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
              <Table.Tr>
                <Table.Th>Applicant</Table.Th>
                <Table.Th ta="center">Projects</Table.Th>
                <Table.Th ta="right">Estimated</Table.Th>
                <Table.Th ta="right">Obligated</Table.Th>
                <Table.Th ta="center">Avg Completion</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {applicantData.map(a => (
                <Table.Tr key={a.name}>
                  <Table.Td>
                    <Text size="xs" fw={600}>{a.name}</Text>
                    <Text size="xs" c="dimmed">{a.role}</Text>
                  </Table.Td>
                  <Table.Td ta="center"><Badge size="sm" variant="light">{a.count}</Badge></Table.Td>
                  <Table.Td ta="right"><Text size="xs">{fmt(a.est)}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="xs">{fmt(a.obl)}</Text></Table.Td>
                  <Table.Td ta="center"><Progress value={a.avgComp} size="sm" style={{ width: 60, display: 'inline-block' }} /><Text size="xs" ml={4} span>{a.avgComp}%</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </div>
        </Paper>

        {/* ── Cost Type Breakdown ──────────────────────────── */}
        <Paper p="md" radius="md" withBorder>
          <Text size="sm" fw={700} mb="sm">Cost Breakdown by Type</Text>
          <Stack gap="sm">
            {costTypeData.map(c => (
              <Stack key={c.type} gap={2}>
                <Group justify="space-between">
                  <Text size="xs" fw={600}>{c.label}</Text>
                  <Group gap="xs">
                    <Text size="xs" c="dimmed">{c.count} items</Text>
                    <Text size="xs" fw={600}>{fmt(c.total)}</Text>
                  </Group>
                </Group>
                <Progress value={pct(c.total, costTotal)} color="var(--data-viz-100)" size="sm" />
              </Stack>
            ))}
            {costTypeData.length === 0 && <Text size="xs" c="dimmed">No cost data.</Text>}
          </Stack>
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* ── Compliance Overview ──────────────────────────── */}
        <Paper p="md" radius="md" withBorder>
          <Group gap="xs" mb="sm">
            <ThemeIcon variant="light" color="var(--color-success)" size="sm"><IconShieldCheck size={14} /></ThemeIcon>
            <Text size="sm" fw={700}>Compliance Overview</Text>
          </Group>
          {complianceStats.total === 0 ? (
            <Text size="xs" c="dimmed">No compliance records.</Text>
          ) : (
            <Stack gap="sm">
              <Group justify="space-between">
                <Text size="xs">NEPA Reviews</Text>
                <Group gap="xs">
                  <Badge size="xs" color="var(--color-success)">{complianceStats.nepaComplete} complete</Badge>
                  <Badge size="xs" color="#C68700">{complianceStats.nepaInProg} in progress</Badge>
                  <Badge size="xs" color="#939598">{complianceStats.total - complianceStats.nepaComplete - complianceStats.nepaInProg} pending</Badge>
                </Group>
              </Group>
              <Progress.Root size="md">
                <Progress.Section value={pct(complianceStats.nepaComplete, complianceStats.total)} color="var(--color-success)" />
                <Progress.Section value={pct(complianceStats.nepaInProg, complianceStats.total)} color="#C68700" />
              </Progress.Root>
              <Group justify="space-between">
                <Text size="xs">Section 106</Text>
                <Group gap="xs">
                  <Badge size="xs" color="var(--color-success)">{complianceStats.s106Complete} done/N/A</Badge>
                  <Badge size="xs" color="#C68700">{complianceStats.s106InProg} in progress</Badge>
                </Group>
              </Group>
              <Progress.Root size="md">
                <Progress.Section value={pct(complianceStats.s106Complete, complianceStats.total)} color="var(--color-success)" />
                <Progress.Section value={pct(complianceStats.s106InProg, complianceStats.total)} color="#C68700" />
              </Progress.Root>
              <Group justify="space-between">
                <Text size="xs">In Floodplain</Text>
                <Badge size="xs" color={complianceStats.floodYes > 0 ? '#F86A0B' : 'var(--color-success)'}>{complianceStats.floodYes} projects</Badge>
              </Group>
              <Group justify="space-between">
                <Text size="xs">Overall Complete</Text>
                <Badge size="xs" color="var(--color-success)">{complianceStats.overallComplete} of {complianceStats.total}</Badge>
              </Group>
            </Stack>
          )}
        </Paper>

        {/* ── Risk Indicators ─────────────────────────────── */}
        <Paper p="md" radius="md" withBorder>
          <Group gap="xs" mb="sm">
            <ThemeIcon variant="light" color="var(--color-error)" size="sm"><IconAlertTriangle size={14} /></ThemeIcon>
            <Text size="sm" fw={700}>Risk Indicators</Text>
          </Group>
          <Stack gap="sm">
            {[
              { label: 'Overdue Projects', items: risks.overdue, color: 'var(--color-error)' },
              { label: 'Deadlines < 30 Days', items: risks.deadlineSoon, color: '#C68700' },
              { label: 'Low Completion (WIP < 20%)', items: risks.lowCompletion, color: '#F86A0B' },
              { label: 'Large Unobligated (> $100K)', items: risks.unobligated, color: 'var(--data-viz-650)' },
            ].map(r => (
              <Paper key={r.label} p="xs" radius="sm" style={{ background: r.items.length > 0 ? 'var(--background-error-light)' : '#f8f9fa' }}>
                <Group justify="space-between">
                  <Text size="xs" fw={600}>{r.label}</Text>
                  <Badge size="sm" color={r.items.length > 0 ? r.color : '#939598'}>{r.items.length}</Badge>
                </Group>
                {r.items.length > 0 && (
                  <Stack gap={2} mt={4}>
                    {r.items.slice(0, 5).map(p => (
                      <Text key={p.Rid} size="xs" c="dimmed">{p.projectNumber} — {p.projectTitle}</Text>
                    ))}
                  </Stack>
                )}
              </Paper>
            ))}
          </Stack>
        </Paper>
      </SimpleGrid>
    </Stack>
  );
}
